"use client";

// Admin-only permissions matrix: toggle which features each role can use.
// Changes take effect immediately (server-side checks read the table).

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api-client";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n/provider";

type Cell = {
  feature: string;
  label: string;
  locked: boolean;
  default: boolean;
  allowed: boolean;
};
type MatrixRow = {
  role: string;
  isAdmin: boolean;
  features: Cell[];
};

const ROLE_LABEL: Record<string, Record<string, string>> = {
  en: { ADMIN: "Admin", OWNER: "Owner", MODERATOR: "Moderator", USER: "User" },
  fr: { ADMIN: "Admin", OWNER: "Propriétaire", MODERATOR: "Modérateur", USER: "Utilisateur" },
  de: { ADMIN: "Admin", OWNER: "Eigentümer", MODERATOR: "Moderator", USER: "Benutzer" },
};

export function PermissionsMatrix() {
  const qc = useQueryClient();
  const { t, locale } = useI18n();

  const { data, isLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: () => apiGet<{ matrix: MatrixRow[] }>("/api/admin/permissions"),
  });
  const matrix = data?.matrix ?? [];
  const roles = matrix.map((r) => r.role);
  const featureRows: { feature: string; label: string; cells: Record<string, Cell> }[] =
    matrix.length > 0
      ? matrix[0].features.map((f) => ({
          feature: f.feature,
          label: f.label,
          cells: Object.fromEntries(
            matrix.map((r) => [r.role, r.features.find((x) => x.feature === f.feature)!])
          ),
        }))
      : [];

  const toggle = async (role: string, feature: string, allowed: boolean) => {
    try {
      await apiPut("/api/admin/permissions", { role, feature, allowed });
      qc.invalidateQueries({ queryKey: ["permissions"] });
      toast.success(t("permissions.updated"));
    } catch (err: any) {
      toast.error(err.message || "Could not update permission");
      qc.invalidateQueries({ queryKey: ["permissions"] });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" /> {t("permissions.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">{t("permissions.hint")}</p>
        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-lg" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 pe-4 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("permissions.feature")}
                  </th>
                  {roles.map((r) => (
                    <th
                      key={r}
                      className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {ROLE_LABEL[locale]?.[r] ?? r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureRows.map((row) => (
                  <tr key={row.feature} className="border-b last:border-0">
                    <td className="py-2.5 pe-4">{t(`permissions.feature.${row.feature}`)}</td>
                    {roles.map((r) => {
                      const cell = row.cells[r];
                      if (!cell) return <td key={r} />;
                      return (
                        <td key={r} className="px-3 py-2.5 text-center">
                          {cell.locked ? (
                            <span className="text-xs text-muted-foreground">✓</span>
                          ) : (
                            <Switch
                              checked={cell.allowed}
                              onCheckedChange={(v) => toggle(r, row.feature, v)}
                              aria-label={`${row.label} — ${ROLE_LABEL[locale]?.[r] ?? r}`}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
